import {
  AfterViewInit,
  Component, ElementRef, Injector,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { debounce } from 'lodash';
import { distinctUntilChanged, firstValueFrom, map, of, Subscription, switchMap } from 'rxjs';
import { ApplicationContext, ChannelEnum, CustomDataConfig } from 'src/app/models';
import { TransactionType } from 'src/app/models/config/loan-purpose.model';
import { UserType } from 'src/app/models/user/user-type.enum';
import { KeyDateType } from 'src/app/models/key-dates/key-date-types.enum';
import { LoanDocTask } from 'src/app/models/loan/loan-doc-task.model';
import { MortgageFundsToCloseCalculationDetails } from 'src/app/models/mortgage-calculation-details.model';
import { ProductPricing } from 'src/app/models/loan/product-pricing.model';
import { PricingVendor } from 'src/app/models/pricing/pricing-vendor';
import { ExternalContactsV2Service } from 'src/app/modules/external-contacts-v2/services/external-contacts-v2.service';
import { PricingUtils } from 'src/app/modules/pricing/pricing-utils';
import { PricingService } from 'src/app/modules/pricing/services/pricing.service';
import { UrlaMenuComponent } from 'src/app/modules/urla/urla-menu/urla-menu.component';
import { Constants } from 'src/app/services/constants';
import { DisclosureProcessingService } from 'src/app/services/disclosure-processing.service';
import { LoanService } from 'src/app/services/loan';
import { MenuService } from 'src/app/services/menu.service';
import { TaskService, TpoTaskFilterEnum } from 'src/app/services/task.service';
import { UrlaValidationService } from 'src/app/services/urla-validation.service';
import { ApplicationContextBoundComponent } from 'src/app/shared/components';
import { blockMenuClicksWhileDisclosuresProcess } from 'src/app/shared/utils/disclosure-processing-utils';
import Swal, { SweetAlertResult } from 'sweetalert2';
import { v4 as uuidv4 } from 'uuid';
import { MenuItemStatus } from '../../models/enums/menu-item-status.enum';
import { MenuItemComponent } from '../menu-item/menu-item.component';
import { TpoFeaturePermissions } from '../tpo-app-details/tpo-app-details.component';

@Component({
  selector: 'tpo-menu-v2',
  templateUrl: './tpo-menu-v2.component.html',
  styleUrls: ['./tpo-menu-v2.component.scss'],
  standalone: false
})
export class TpoMenuV2Component extends ApplicationContextBoundComponent implements OnInit, AfterViewInit, OnDestroy {

  // Status / channel groupings hoisted out of the template. The status checks run inside the
  // reusable #statusIndicator ng-template (instantiated per menu item), so inline array literals
  // were re-allocated on every change-detection pass; Sets allocate once. Both Capitalized and
  // lowercase status variants are kept to preserve original behavior.
  protected readonly successOrRequestedStatuses = new Set<string>(['Success', 'success', 'Requested', 'requested']);
  protected readonly successStatuses = new Set<string>(['Success', 'success']);
  protected readonly requestedStatuses = new Set<string>(['Requested', 'requested']);
  protected readonly pendingStatuses = new Set<string>(['Pending', 'pending']);
  protected readonly errorStatuses = new Set<string>(['Error', 'error']);
  protected readonly loadingStatuses = new Set<string>(['Loading', 'loading']);
  protected readonly correspondentChannels = new Set<string>(['Correspondent', 'NonDelegatedCorrespondent']);

  @ViewChild('urlaMenu') urlaMenu: UrlaMenuComponent;

  @ViewChildren(MenuItemComponent) menuItems: QueryList<MenuItemComponent> | undefined;

  @Input()
  featurePermissions: TpoFeaturePermissions;

  @Input()
  showStepLabels: boolean;

  @Input()
  set stepStatuses(value: { [key: string]: string }) {
    this._stepStatuses = value;
    this.populateInitialMenuItemStatuses();
  }

  get stepStatuses() {
    return this._stepStatuses;
  }

  @Input()
  set disclosuresEnabled(value: boolean) {
    this._disclosuresEnabled = value;
    if (!value) {
      this.isDisclosuresVisible = false;
    }
  }

  get disclosuresEnabled() {
    return this._disclosuresEnabled;
  }

  tab: string;

  fileArchived: boolean = false;
  isUrlaReadOnly: boolean = false;
  isPRMG: boolean = false;
  isDeephaven: boolean = false;
  isCompanyPulseProcessing: boolean = false;
  isSmartfi: boolean = false;
  isBrokerDisclosedAndSubmitted: boolean = false;
  isReverseMortgage: boolean = false;
  tpoSubmitted: boolean = false;

  stepOrder: { [key: string]: number } = {};

  disabledTpoAppMenuItems: readonly string[] = [];

  protected uniqueId: string;
  protected channel: string;

  protected pricingVendorsAreOnlyIframes: boolean = false;
  protected pollyIframeScenarioPricingEnabled: boolean = false;
  private _pollyIframeScenarioPricingCacheKey: string | null = null;
  protected isManualPricingEntryAvailable: boolean = false;
  protected isFeesVisible: boolean;
  protected isDisclosuresVisible: boolean = false;
  protected isCocVisible: boolean;

  protected disclosuresPending: boolean = false;

  protected loanId: number;
  protected hasLosLink: boolean = false;

  protected isCdRequestApplicable: boolean = false;
  protected isCorrIcdRequestApplicable: boolean = false;
  protected hasApprovedWithConditionsDate: boolean = false;

  protected showUWDecision: boolean = false;

  protected defaultShowArchiveCondition = false;
  protected showArchiveCondition: boolean = false;
  protected isMiQuoteVisible: boolean = false;

  protected expandedMenuId: string | null = null;

  private _urlaMenuItemStatusSubscription: Subscription;
  private _routerEventSubscription: Subscription;
  private _activatedRouteSubscription: Subscription;
  private _disclosureProcessingSubscription: Subscription;
  private _contextSubscription: Subscription;
  private _menuStatusChangesSubscription: Subscription;
  private _loanChangesSubscription: Subscription;
  private _externalContactsSubscription: Subscription;
  private _submissionTasksSubscription: Subscription;
  private _appRequestedKeyDatesSubscription: Subscription;
  private _disclosureProcessingClickBlocker: (() => void) | null = null;

  protected readonly MenuItemStatus = MenuItemStatus;

  private _stepStatuses: { [key: string]: string } = {};
  private _disclosuresEnabled: boolean = true;

  /**
   * Loan Summary becomes a real, gated step for non-GoProcessing companies when the Custom Info section is
   * turned on for the TPO loan summary and at least one of the custom fields shown there is required.
   */
  protected isLoanSummaryStepRequiredForCustomInfo: boolean = false;

  private _loanSummaryStatusBasedOnExternalContacts: MenuItemStatus = MenuItemStatus.Pending;
  private _loanSummaryStatusBasedOnLoanFields: MenuItemStatus = MenuItemStatus.Pending;
  private readonly _urlaSubMenuItemIds = new Set<string>(
    Object.values(Constants.menu.urlaMenuItems)
  );

  constructor(
    private readonly injector: Injector,
    private readonly _menuService: MenuService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _loanService: LoanService,
    private readonly _urlaValidationService: UrlaValidationService,
    private readonly _externalContactsV2Service: ExternalContactsV2Service,
    private readonly _taskService: TaskService,
    private readonly _pricingService: PricingService,
    private readonly _disclosureProcessingService: DisclosureProcessingService,
    private readonly _elementRef: ElementRef<HTMLElement>
  ) {
    super(injector);
    this.isPRMG = this.applicationContext.isCompanyPRMG;
    this.isDeephaven = this.applicationContext.isCompanyDeepHaven;
    this.isCompanyPulseProcessing = this.applicationContext.isCompanyPulseProcessing;
    this.isSmartfi = this.applicationContext.isCompanySmartfi;
    this.uniqueId = uuidv4();
    this._routerEventSubscription = this._router.events.subscribe(
      (event) => {
        if (event instanceof NavigationEnd) {
          this._getInitTab();
        }
      }
    );

    this._externalContactsSubscription =
      this.applicationContextService.externalContactAssignmentsChangedEvent.subscribe(
        async () => {
          await this.setMenuStatusesBasedOnSeveralValidations();
        }
      );

    this._submissionTasksSubscription =
      this.applicationContextService.submissionTasksChangedEvent.subscribe(
        async () => {
          await this.setMenuStatusesBasedOnSeveralValidations();
        }
      );

    this._appRequestedKeyDatesSubscription =
      this.applicationContextService.applicationRequestedKeyDateChangedEvent.subscribe(
        async () => {
          await this.setMenuStatusesBasedOnSeveralValidations();
        }
      );

    this._contextSubscription =
      this.applicationContextService.context.subscribe((context) => {
        this.pricingVendorsAreOnlyIframes =
          this.checkIfPricingVendorsAreOnlyIframesOrNot(context);
        this.initializeDebounce(context);
      });

    this._menuStatusChangesSubscription =
      this.applicationContextService.menuStatusChanges.subscribe((context) => {
        console.log('menu status refresh called');
        this.initializeDebounce(context);
      });

    this._loanChangesSubscription =
      this.applicationContextService.loanInfoChanges.subscribe(({ context }) => {
        this.initializeDebounce(context);
      });
  }

  async ngOnInit() {
    this._activatedRouteSubscription = this._activatedRoute.params.subscribe(
      (params) => {
        this._getInitTab();
      }
    );

    this._disclosureProcessingSubscription = this.applicationContextService.context
      .pipe(
        map(context => context?.isCompanyPRMG ? context.application?.applicationId : null),
        distinctUntilChanged(),
        switchMap(applicationId => applicationId
          ? this._disclosureProcessingService.isProcessing$(applicationId)
          : of(false))
      )
      .subscribe(isProcessing => {
        this.disclosuresPending = isProcessing;
      });

    await this.initialize(this.applicationContext);
  }

  ngAfterViewInit(): void {
    this._disclosureProcessingClickBlocker = blockMenuClicksWhileDisclosuresProcess(
      this._elementRef.nativeElement,
      () => this.disclosuresPending
    );

    this.isReverseMortgage =
      this.applicationContext.globalConfig.loanPurpose.find(
        (x) =>
          x.loanPurposeId == this.applicationContext?.application?.loanPurposeId
      )?.transactionType == TransactionType.ReverseMortgage;

    this.applicationContext.application.mortgageLoan.isReverseMortgage =
      this.isReverseMortgage;

    this.applicationContextService.getMortgageCalculationDetails(this.applicationContext.currentMortgage.mortgageId).subscribe((details) => {
      this.setMenuItemStatuses(details.fundsToClose);
    });
  }

  private async setMenuItemStatuses(ftc: MortgageFundsToCloseCalculationDetails) {
    const urlaFieldsConfig =
      await this._urlaValidationService.getUrlaFieldsConfigForMortgage(
        this.applicationContext.application.mortgageLoan,
        !!this.applicationContext.application.losIdentifier,
        true
      );

    const urlaStatus = this._urlaValidationService.getStatusForUrla(
      urlaFieldsConfig,
      this.applicationContext.application,
      ftc,
      this.featurePermissions.isMersEnabled,
      this.featurePermissions.isRequiredLoanAndCaseNumbers,
      true
    );

    this.stepStatuses.urla = urlaStatus;
    this._menuService.setStatus('urla', urlaStatus);
    this._menuService.setUrlaStatus(urlaStatus);

    this.populateInitialMenuItemStatuses();
    this._urlaMenuItemStatusSubscription =
      this._menuService.menuItemStatusChanged.subscribe((e) => {
        const menuItem = this.menuItems?.find((mi) => mi.id === e.id);
        if (menuItem) {
          menuItem.status = e.status;
        }
        if (this._urlaSubMenuItemIds.has(e.id)) {
          this.stepStatuses[e.id] = e.status;
          this.updateUrlaParentStatusFromChildStatuses();
        }
        if (e.id === 'loanSummary') {
          this._loanSummaryStatusBasedOnLoanFields = e.status;
          // External contacts only gate the GoProcessing loan summary page.
          this.stepStatuses.loanSummary = this.isCompanyPulseProcessing
            ? this.determineDominantStatus([
              this._loanSummaryStatusBasedOnExternalContacts,
              e.status,
            ])
            : e.status;
        }

        setTimeout(() => {
          if (this.urlaMenu) {
            if (
              this.urlaMenu.menuItems.some(
                (mi) =>
                  mi.groupId === 'urla' && mi.status === MenuItemStatus.Error
              )
            ) {
              this.stepStatuses.urla = MenuItemStatus.Error;
              this._menuService.urlaStatusChanged.emit(MenuItemStatus.Error);
            } else if (
              this.urlaMenu.menuItems.some(
                (mi) =>
                  mi.groupId === 'urla' && mi.status === MenuItemStatus.Pending
              )
            ) {
              this.stepStatuses.urla = MenuItemStatus.Pending;
              this._menuService.urlaStatusChanged.emit(MenuItemStatus.Pending);
            } else {
              this.stepStatuses.urla = MenuItemStatus.Success;
              this._menuService.urlaStatusChanged.emit(MenuItemStatus.Success);
            }
          }
        }, 200);
      });
  }

  private updateUrlaParentStatusFromChildStatuses = () => {
    const urlaChildStatuses = Array.from(this._urlaSubMenuItemIds)
      .map((id) => this.stepStatuses[id])
      .filter((status) => !!status) as MenuItemStatus[];

    if (!urlaChildStatuses.length) {
      return;
    }

    const urlaStatus = this.determineDominantStatus(urlaChildStatuses);
    this.stepStatuses.urla = urlaStatus;
    this._menuService.setUrlaStatus(urlaStatus);
  };

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._urlaMenuItemStatusSubscription?.unsubscribe();
    this._activatedRouteSubscription?.unsubscribe();
    this._routerEventSubscription?.unsubscribe();
    this._contextSubscription?.unsubscribe();
    this._loanChangesSubscription?.unsubscribe();
    this._disclosureProcessingSubscription?.unsubscribe();
    this._menuStatusChangesSubscription?.unsubscribe();
    this._externalContactsSubscription?.unsubscribe();
    this._submissionTasksSubscription?.unsubscribe();
    this._appRequestedKeyDatesSubscription?.unsubscribe();
    this._disclosureProcessingClickBlocker?.();
  }

  onListItemClicked = (e: any, tabToActivate: string) => {
    if (this.tab === tabToActivate) {
      e.preventDefault();
      return;
    }
    this.tab = tabToActivate;
  };

  onArchiveLoanClicked = () => {
    Swal.fire({
      title: 'Are you sure?',
      text: 'Are you sure you want to archive this loan?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes',
      cancelButtonText: 'No',
      reverseButtons: true,
    }).then(async (result: SweetAlertResult) => {
      if (!result.value) {
        return;
      }
      const request = {
        TpoArchiveRequested: new Date().toISOString(),
      };
      await firstValueFrom(
        this._loanService.saveKeyDatesByType(
          request,
          this.applicationContext.application.applicationId
        )
      );

      this.fileArchived = true;
      this.isUrlaReadOnly = true;
    });
  };

  onMenuItemClicked = (id: string) => {
    this._menuService.onMenuItemClicked(id);
  };

  private isLockActive(productPricing: ProductPricing | null | undefined): boolean {
    if (productPricing?.lockStatus !== 'Accepted') {
      return false;
    }

    if (!productPricing.lockExpirationDate) {
      return true;
    }

    const lockExpirationDate = productPricing.lockExpirationDate.replace(
      'T00:00:00.000Z',
      'T23:59:59.999Z'
    );
    const expirationDate = Date.parse(lockExpirationDate);
    const now = new Date().getTime();
    return !(now >= expirationDate);
  }

  initialize = async (ctx: ApplicationContext) => {
    if (!ctx.application) {
      return;
    }

    this.disabledTpoAppMenuItems = ctx.userPermissions.disabledTPOAppMenuItems;
    this.loanId = ctx.application.applicationId;
    this.hasLosLink = !!ctx.application.losIdentifier;
    this.pricingVendorsAreOnlyIframes =
      this.checkIfPricingVendorsAreOnlyIframesOrNot(ctx);

    await this.refreshPollyIframeScenarioPricingEnabled(ctx);

    this.isCdRequestApplicable =
      ctx.application.channel === ChannelEnum.Wholesale &&
      this.isLockActive(ctx.application.productPricing);

    this.isCorrIcdRequestApplicable =
      ctx.globalConfig?.useCorrIcdRequestFlow
      && ctx.application.channel === ChannelEnum.NonDelegatedCorrespondent
      && this.isLockActive(ctx.application.productPricing);
    try {
      const keyDates = ctx.applicationKeyDatesByType;
      if (ctx.application.channel == ChannelEnum.Wholesale || ctx.application.channel == ChannelEnum.NonDelegatedCorrespondent) {
        if (!ctx.tpoConfig?.allowUrlaEditsPostSubmission) {
          if (keyDates?.[KeyDateType.LEIssued]?.eventDate
            || keyDates?.[KeyDateType.InitialDisclosureSent]?.eventDate
            || keyDates?.[KeyDateType.TpoSubmission]?.eventDate
            || keyDates?.[KeyDateType.TpoManualDisclosureRequested]?.eventDate) {
            this.isUrlaReadOnly = true;
          }
        }
      }
      else if (ctx.application.channel == ChannelEnum.Correspondent) {
        if (keyDates?.[KeyDateType.Purchased]?.eventDate) {
          this.isUrlaReadOnly = true;
        }
      }

      if (keyDates?.[KeyDateType.ApprovedWithConditions]?.eventDate) {
        this.hasApprovedWithConditionsDate = true;
      }

      if (keyDates?.[KeyDateType.TpoArchiveRequested]?.eventDate) {
        this.fileArchived = true;
        this.isUrlaReadOnly = true;
      }
    } catch (error) {
      console.error(error);
    }

    this.stepStatuses = { ...this.stepStatuses, ...ctx.tpoMenuStatuses };
    const statusItem = this.stepStatuses[Constants.menu.servicesMenuItems.submission];
    this.tpoSubmitted =
      statusItem && statusItem.toLowerCase() == MenuItemStatus.Success;
    if (this.tpoSubmitted && !ctx.tpoConfig?.allowUrlaEditsPostSubmission) {
      this.isUrlaReadOnly = true;
    }

    this.isBrokerDisclosedAndSubmitted =
      ctx.application?.disclosurePath == 'Broker';

    this.isManualPricingEntryAvailable = ctx.userPermissions.companyId == 302;

    this.channel = ctx.application.channel;

    this.isFeesVisible =
      this.featurePermissions?.isFeesEnabled &&
      this.channel != 'Correspondent' &&
      !(
        this.isPRMG &&
        this.channel === 'NonDelegatedCorrespondent' &&
        this.applicationContext.application.nonDelOptions !==
        'AllDisclosuresThroughLender'
      ) && !this.applicationContext.tpoConfig.disableFees;

    this.isDisclosuresVisible =
      this.disclosuresEnabled && (this.featurePermissions?.isDisclosureGenerationAllowed ||
        this.isBrokerDisclosedAndSubmitted) &&
      this.channel != 'Correspondent' &&
      !(
        this.isPRMG &&
        this.channel === 'NonDelegatedCorrespondent' &&
        this.applicationContext.application.nonDelOptions !==
        'AllDisclosuresThroughLender'
      );

    this.isCocVisible = !this.isPRMG || this.channel !== 'Correspondent';

    this.isLoanSummaryStepRequiredForCustomInfo =
      !this.isCompanyPulseProcessing &&
      ctx.tpoConfig?.showCustomInfoOnTPOLoanSummary === true &&
      this.getTpoCustomDataConfig(ctx).some(cd => cd.required);

    this.setStepOrders();

    this.getRecentUW(this.loanId);

    this.defaultShowArchiveCondition = (this.isPRMG || this.isDeephaven) && !this.hasLosLink && !this.fileArchived && !this.disabledTpoAppMenuItems?.includes('ArchiveLoan')

    if (ctx.tpoConfig?.allowArchiveExpressionGroupId && ctx.tpoConfig?.allowArchiveExpressionGroup) {
      this.showArchiveCondition = await this.getAllowArchive();
    } else {
      this.showArchiveCondition = this.defaultShowArchiveCondition;
    }

    if (ctx.tpoConfig?.miQuoteExpressionGroupId && ctx.tpoConfig?.miQuoteExpressionGroup) {
      this.isMiQuoteVisible = await this.getIsMiScreenVisible();
    } else {
      this.isMiQuoteVisible = false;
    }

    await this.setMenuStatusesBasedOnSeveralValidations();
  };

  initializeDebounce = debounce(this.initialize, 1000);

  private setMenuStatusesBasedOnSeveralValidations = async () => {
    if (this.isCompanyPulseProcessing) {

      const loanSummaryStatusBasedOnExternalContacts =
        await this.evaluateValidationStatusForExternalContacts();
      this._loanSummaryStatusBasedOnExternalContacts = loanSummaryStatusBasedOnExternalContacts;

      // Always recalculate loan fields status to avoid timing issues
      this._loanSummaryStatusBasedOnLoanFields = this.getPulseProcessingLoanSummaryStatus();

      const loanSummaryStepStatus = this.determineDominantStatus([
        loanSummaryStatusBasedOnExternalContacts,
        this._loanSummaryStatusBasedOnLoanFields,
      ]);

      this.stepStatuses.loanSummary = loanSummaryStepStatus;

      const submissionStatusBasedOnResultsOfSubmissionValidationApi =
        await this.getSubmissionStatusBasedOnResultsOfSubmissionValidationApi();

      const submissionStatus = this.determineDominantStatus([
        submissionStatusBasedOnResultsOfSubmissionValidationApi,
      ]);
      this.stepStatuses.submission = submissionStatus;
    } else if (this.isLoanSummaryStepRequiredForCustomInfo) {
      this._loanSummaryStatusBasedOnLoanFields =
        this._urlaValidationService.getRequiredCustomDataStatus(
          this.getTpoCustomDataConfig(this.applicationContext),
          this.applicationContext.customData
        );
      this.stepStatuses.loanSummary = this._loanSummaryStatusBasedOnLoanFields;
    } else if (this.isSmartfi) {
      const keyDatesByType = this.applicationContext.applicationKeyDatesByType;
      if (keyDatesByType[KeyDateType.ApplicationRequested]?.eventDate) {
        this.stepStatuses.disclosure = MenuItemStatus.Success;
      }
    }

    await this.evaluateValidationStatusForEsignTasks();
  }

  private setStepOrders = () => {

    const isDisabled = (key: string) => this.disabledTpoAppMenuItems?.includes(key) === true;
    const canSeeUrla = () => !isDisabled('Urla');


    const canSeeServices = () =>
      this.featurePermissions?.isServicesEnabled === true &&
      this.channel !== 'Correspondent' &&
      !this.isSmartfi &&
      !isDisabled('Services');

    const canSeeLoanSummaryStep = () =>
      (this.isCompanyPulseProcessing === true || this.isLoanSummaryStepRequiredForCustomInfo) &&
      this.tpoSubmitted !== true &&
      !isDisabled('LoanSummary');

    const canSeePricing = () =>
      (this.featurePermissions?.pricingEnabled === true || this.isManualPricingEntryAvailable === true) &&
      !isDisabled('Pricing');

    const canSeeFees = () => this.isFeesVisible === true && !isDisabled('Fees');

    const canSeeDisclosures = () => this.isDisclosuresVisible === true && !isDisabled('Disclosures');

    const canSeeSubmission = () => this.tpoSubmitted !== true;

    const steps: string[] = [];

    if (!this.isReverseMortgage) {
      // Required custom fields have to be filled out first, so the section leads the workflow when it gates it.
      if (canSeeLoanSummaryStep() && this.isLoanSummaryStepRequiredForCustomInfo) steps.push('loanSummary');
      if (canSeeUrla()) steps.push('urla');
      if (canSeeServices()) steps.push('services');
      if (canSeeLoanSummaryStep() && !this.isLoanSummaryStepRequiredForCustomInfo) steps.push('loanSummary');
      if (canSeePricing()) steps.push('pricing');
      if (canSeeFees()) steps.push('fees');
      if (canSeeDisclosures()) steps.push('disclosures');
      if (canSeeSubmission()) steps.push('submission');
    } else {
      if (canSeePricing()) steps.push('pricing');
      if (canSeeUrla()) steps.push('urla');
      if (canSeeFees()) steps.push('fees');
      if (canSeeDisclosures()) steps.push('disclosures');
      if (canSeeSubmission()) steps.push('submission');
    }

    steps.forEach((key, index) => {
      this.stepOrder[key] = index + 1;
    });
  };

  private evaluateValidationStatusForExternalContacts = async () => {
    try {
      const externalContacts = await firstValueFrom(this._externalContactsV2Service.getExternalContacts(this.applicationContext.application.applicationId));
      const agentTypesRequiredForSubmission = this.applicationContext.globalConfig.agentType.filter((at) => at.isRequiredForTPOSubmission);
      if (!agentTypesRequiredForSubmission || agentTypesRequiredForSubmission.length == 0) {
        // No need to validate anything, there are no agent types required for submission
        return MenuItemStatus.Success;
      }
      if (externalContacts && externalContacts.length > 0) {
        // Now look at the external contact agents and see if they have the required agent types
        const missingAgentTypes: string[] = [];
        agentTypesRequiredForSubmission.forEach((at) => {
          if (!externalContacts.some((ec) => ec.agentType?.agentTypeId == at.agentTypeId)) {
            missingAgentTypes.push(at.agentTypeName);
          }
        });
        if (missingAgentTypes.length > 0) {
          return MenuItemStatus.Error;
        }
      } else {
        return MenuItemStatus.Error;
      }
      return MenuItemStatus.Success;
    } catch {
      return undefined;
    }
  };

  private evaluateValidationStatusForEsignTasks = async () => {
    try {
      const tasks: LoanDocTask[] = await firstValueFrom(this._taskService.getAllTpoTasksByLoanAndByFilter(this.applicationContext.application.applicationId, TpoTaskFilterEnum.All));
      const eSignTasks = tasks.filter(t => ['EsignDocument', 'LosEsign'].includes(t.taskType) && t.taskStatus == 'Pending');

      if (eSignTasks.length) {
        this.stepStatuses.disclosure = MenuItemStatus.Requested;
      }
    } catch {
      return;
    }
  };

  private populateInitialMenuItemStatuses = () => {
    if (!this.menuItems) {
      return;
    }
    if (this.applicationContext.isCompanyPulseProcessing) {
      this.stepStatuses.fees = MenuItemStatus.Success;
    }
    if (this.applicationContext.isCompanyPRMG && this.applicationContext.application.channel === 'NonDelegatedCorrespondent') {
      this.stepStatuses.fees = this.stepStatuses.fees === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses.fees;
      this.stepStatuses.disclosure = this.stepStatuses.disclosure === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses.disclosure;
      this.stepStatuses.pricing = this.stepStatuses.pricing === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses.pricing;
      if (this.applicationContext.application.nonDelOptions === 'AllDisclosuresThroughLender') {
        this.stepStatuses['aus'] = this.stepStatuses.aus === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses.aus;
        this.stepStatuses['credit'] = this.stepStatuses.credit === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses.credit;
        this.stepStatuses['voa'] = this.stepStatuses.credit === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses['voa'];
        this.stepStatuses['voi-voe'] = this.stepStatuses.credit === MenuItemStatus.Pending ? MenuItemStatus.Error : this.stepStatuses['voi-voe'];
      }
    }
    if (this.isCompanyPulseProcessing) {
      this._loanSummaryStatusBasedOnLoanFields = this.getPulseProcessingLoanSummaryStatus();
    }
    Object.keys(this.stepStatuses).forEach((key) => {
      const menuItem = this.menuItems.find((mi) => mi.id === key);
      if (menuItem) {
        menuItem.status = this.stepStatuses[key]?.toLowerCase() as MenuItemStatus;
        if (this.isCompanyPulseProcessing && menuItem.id === Constants.menu.servicesMenuItems.fees) {
          menuItem.status = MenuItemStatus.Success;
        }
      }
    });
    this.setServicesStepStatus(this.stepStatuses);
  };

  private setServicesStepStatus = (allStatuses: any) => {
    const servicesSubMenuIds = [
      this.featurePermissions.isCreditAllowed
        ? Constants.menu.servicesMenuItems.credit
        : null,
      this.featurePermissions.isAusAllowed
        ? Constants.menu.servicesMenuItems.aus
        : null,
      this.featurePermissions.isVoaAllowed
        ? Constants.menu.servicesMenuItems.voa
        : null,
      this.featurePermissions.isVoiVoeAllowed
        ? Constants.menu.servicesMenuItems.voiVoe
        : null,
    ].filter((s) => !!s);

    const servicesStatuses = Object.keys(allStatuses)
      .filter((k) => servicesSubMenuIds.includes(k))
      .map((k) => allStatuses[k]);
    this.stepStatuses.services = this.determineDominantStatus([
      ...servicesStatuses,
    ]);
    if (this.isCompanyPulseProcessing && this.stepStatuses.services === MenuItemStatus.Pending) {
      this.stepStatuses.services = MenuItemStatus.Success;
    }
  };

  private determineDominantStatus = (
    statuses: MenuItemStatus[]
  ): MenuItemStatus => {
    const arr = statuses.map((s) => s?.toLowerCase());

    if (arr.includes('loading')) {
      return MenuItemStatus.Loading;
    } else if (arr.includes('error')) {
      return MenuItemStatus.Error;
    } else if (arr.includes('pending')) {
      return MenuItemStatus.Pending;
    } else {
      return MenuItemStatus.Success;
    }
  };

  // Accordion toggle: collapse if already expanded, otherwise expand (collapsing any other)
  protected toggleMenuExpansion = (menuId: string) => {
    this.expandedMenuId = this.expandedMenuId === menuId ? null : menuId;
  };

  // Opening a section from its row link always expands it; navigation only auto-expands when the route changes.
  protected expandMenu = (menuId: string) => {
    this.expandedMenuId = menuId;
  };

  protected get isServicesExpanded(): boolean {
    return this.expandedMenuId === 'services';
  }

  protected get isContactsExpanded(): boolean {
    return this.expandedMenuId === 'contacts';
  }

  protected get isUrlaExpanded(): boolean {
    return this.expandedMenuId === 'urla';
  }

  protected get isPricingExpanded(): boolean {
    return this.expandedMenuId === 'pricing';
  }

  private _getInitTab = () => {
    const splitUrl = this._router.url.split("/");
    const previousTab = this.tab;

    setTimeout(() => {
      // Determine current tab from URL
      if (splitUrl.length < 5) {
        this.tab = 'loan-summary';
      } else {
        const pageType = splitUrl[splitUrl.length - 1];
        this.tab = pageType ? pageType : 'loan-summary';
      }

      // Only auto-expand when navigating to a different route
      if (previousTab !== this.tab) {
        this.expandedMenuId = this._getMenuIdForRoute(this.tab);
      }
    });
  };

  // The section holding the current page. Row highlighting follows this; the chevron shows what is expanded.
  protected get activeMenuId(): string | null {
    return this._getMenuIdForRoute(this.tab);
  }

  // Map routes to their parent menu for auto-expansion
  private _getMenuIdForRoute(tab: string): string | null {
    const menuRoutes: Record<string, string[]> = {
      services: ['credit-reports', 'digital-emp-income-verification', 'digital-asset-verification', 'aus', 'mi-quoting', 'link', 'link-income-calculator', 'income-analysis'],
      contacts: ['change-of-circumstance', 'escalation-history', 'loan-docs-v3', 'internal-contacts', 'external-contacts', 'key-dates', 'loan-activity', 'esignature-history'],
      pricing: ['pricing', 'pricing-history', 'application-pricing-scenarios', 'amortization'],
      urla: ['urla']
    };

    for (const [menuId, routes] of Object.entries(menuRoutes)) {
      if (routes.includes(tab)) return menuId;
    }
    return null;
  }

  onUrlaMenuItemClicked = (menuItemId: string) => {
    this.tab = 'urla';
    setTimeout(() => {
      this._menuService.onMenuItemClicked(menuItemId);
    });
  }

  // Polly iframe companies get the scenarios submenu when the company has scenario pricing turned on for the loan's
  // channel, so the credentials are re-read whenever the loan or its channel changes.
  private refreshPollyIframeScenarioPricingEnabled = async (
    context: ApplicationContext
  ): Promise<void> => {
    const applicationId = context.application.applicationId;
    const cacheKey = `${applicationId}|${context.application.channel}`;

    if (!this.pricingVendorsAreOnlyIframes) {
      this.pollyIframeScenarioPricingEnabled = false;
      this._pollyIframeScenarioPricingCacheKey = null;
      return;
    }

    if (this._pollyIframeScenarioPricingCacheKey === cacheKey) {
      return;
    }

    this._pollyIframeScenarioPricingCacheKey = cacheKey;
    this.pollyIframeScenarioPricingEnabled = false;

    try {
      const pricingCredentials = await firstValueFrom(
        this._pricingService.getPricingCredentials(applicationId)
      );

      // A loan or channel change starts a new request, so an older response no longer describes the current context.
      if (this._pollyIframeScenarioPricingCacheKey !== cacheKey) {
        return;
      }

      this.pollyIframeScenarioPricingEnabled =
        PricingUtils.isPollyIframeScenarioPricingEnabled(pricingCredentials);
    } catch (e) {
      if (this._pollyIframeScenarioPricingCacheKey !== cacheKey) {
        return;
      }

      this.pollyIframeScenarioPricingEnabled = false;
      this._pollyIframeScenarioPricingCacheKey = null;
    }
  };

  private checkIfPricingVendorsAreOnlyIframesOrNot = (
    context: ApplicationContext
  ): boolean => {
    const pricingEngines = context.userPermissions.enabledPricingEngines;
    let thereIsAtLeastOneVendorThatIsNotIframe = false;
    if (pricingEngines && pricingEngines.length > 0) {
      pricingEngines.forEach((pe) => {
        if (
          pe !== PricingVendor.LenderPriceIframe &&
          pe !== PricingVendor.LoanPassIframe &&
          pe !== PricingVendor.PollyIframe
        ) {
          thereIsAtLeastOneVendorThatIsNotIframe = true;
        }
      });
    }
    return !thereIsAtLeastOneVendorThatIsNotIframe;
  };

  private getSubmissionStatusBasedOnResultsOfSubmissionValidationApi =
    async () => {
      try {
        const validationResults = await firstValueFrom(
          this._loanService.validateForSubmission(
            this.applicationContext.application.applicationId
          )
        );
        const isValidForSubmission = validationResults?.passed;
        if (!isValidForSubmission) {
          return MenuItemStatus.Error;
        }
        return MenuItemStatus.Success;
      } catch {
        return undefined;
      }
    };

  private getAllowArchive =
    async () => {
      try {
        const validationResults = await firstValueFrom(
          this._loanService.allowArchive(
            this.applicationContext.application.applicationId
          )
        );
        return validationResults.isArchiveAllowed;
      } catch (error) {
        return false;
      }
    };

  private getIsMiScreenVisible =
    async () => {
      try {
        const result = await firstValueFrom(
          this._loanService.isMiScreenVisible(
            this.applicationContext.application.applicationId
          )
        );
        return result.isMiQuoteVisible;
      } catch (error) {
        return false;
      }
    };

  private getRecentUW = (loanId: number) => {
    this._loanService.getRecentUWDoc(loanId).subscribe({
      next: (response) => {
        if (response) {
          this.showUWDecision = true;
        }
      },
    });
  };

  /** Custom data fields configured to show on the TPO loan summary for TPO users. */
  private getTpoCustomDataConfig = (ctx: ApplicationContext): CustomDataConfig[] =>
    (ctx.globalConfig?.customDataConfig || [])
      .filter(cd => !!cd.layouts?.find(l => l.roleType == UserType.Tpo && l.uiRow));

  private getPulseProcessingLoanSummaryStatus = () => {
    const status = this._urlaValidationService.getPulseProcessingLoanSummaryStatus(this.applicationContext.application.mortgageLoan, this.applicationContext.application,
      this.applicationContext.globalConfig.customDataConfig, this.applicationContext.customData);
    return status;
  }
}

export class ParentMenuItemDescriptor {
  listItemId: string;
  childListId: string;
}
